// templateUtils.js — pure utility functions, no React

import { getHotelLanguageCode, getTranslation } from '../../Base/Common/ReferenceDataFunctions';
import { sortByHotelLanguage } from '../../Base/Common/CommonUIComponents';

const MM_TO_PX_96DPI = 96 / 25.4;

export const TEMPLATE_LANGUAGES = [
    { code: 'pt', flagClass: 'flag-icon flag-icon-pt', label: 'PT' },
    { code: 'en', flagClass: 'flag-icon flag-icon-gb', label: 'EN' },
    { code: 'fr', flagClass: 'flag-icon flag-icon-fr', label: 'FR' },
    { code: 'de', flagClass: 'flag-icon flag-icon-de', label: 'DE' },
    { code: 'es', flagClass: 'flag-icon flag-icon-es', label: 'ES' },
];
export const DEFAULT_LANG = 'en';

// Language the designer opens with: the hotel language when the designer has it, otherwise DEFAULT_LANG
export const getInitialPreviewLang = () => {
    const hotelLang = getHotelLanguageCode().toLowerCase();
    return TEMPLATE_LANGUAGES.some(l => l.code === hotelLang) ? hotelLang : DEFAULT_LANG;
};

// Designer language buttons: hotel language first, then the base order
export const getOrderedTemplateLanguages = () => sortByHotelLanguage(TEMPLATE_LANGUAGES, l => l.code);

// Migrate freeText from legacy string to { pt, en, fr, de, es } object
export const normalizeFreeText = (freeText) => {
    if (freeText && typeof freeText === 'object') return freeText;
    const str = typeof freeText === 'string' ? freeText : '';
    return { pt: str, en: str, fr: str, de: str, es: str };
};

// Get display text for preview/print with the shared fallback: requested → hotel language → en → first non-empty → ''
export const getPreviewText = (freeText, lang) => {
    const obj = normalizeFreeText(freeText);
    const texts = TEMPLATE_LANGUAGES.map(l => ({ languageCode: l.code, text: obj[l.code] }));
    return getTranslation(texts, 'text', lang)?.text || '';
};

export const mmToPx = (mm, zoom = 1) => Math.round(mm * MM_TO_PX_96DPI * zoom);
export const pxToMm = (px, zoom = 1) => Math.round(px / (MM_TO_PX_96DPI * zoom) * 10) / 10;

// Paper dimensions in px at 96dpi (used for canvas rendering)
export const PAGE_SIZES = {
    A4: { portrait: { w: 794, h: 1123 }, landscape: { w: 1123, h: 794 } },
    A5: { portrait: { w: 559, h: 794 }, landscape: { w: 794, h: 559 } },
};

// Exact ISO paper dimensions in mm (used for print HTML wrapper — avoids rounding drift)
const PAPER_DIMS_MM = {
    A4: { portrait: { w: 210, h: 297 }, landscape: { w: 297, h: 210 } },
    A5: { portrait: { w: 148, h: 210 }, landscape: { w: 210, h: 148 } },
};

export const getPaperDimsMm = (pageSize, orientation) => {
    const size = PAPER_DIMS_MM[pageSize] || PAPER_DIMS_MM.A4;
    return size[orientation] || size.portrait;
};

export const getPageDimsPx = (pageSize, orientation) => {
    const size = PAGE_SIZES[pageSize] || PAGE_SIZES.A4;
    return size[orientation] || size.portrait;
};

// Width/height in mm derived from px dims
export const getPageDimsMm = (pageSize, orientation) => {
    const px = getPageDimsPx(pageSize, orientation);
    return {
        w: Math.round(px.w / MM_TO_PX_96DPI * 10) / 10,
        h: Math.round(px.h / MM_TO_PX_96DPI * 10) / 10,
    };
};

// Simple 9-char random id (no external dependency)
export const generateId = () =>
    Math.random().toString(36).slice(2, 11);

// Default element properties by type
export const createDefaultElement = (type, canvasWMm, canvasHMm) => {
    const cx = Math.round(canvasWMm / 2 * 10) / 10;
    const cy = Math.round(canvasHMm / 2 * 10) / 10;

    const base = {
        id: generateId(),
        type,
        x: Math.max(0, cx - 40),
        y: Math.max(0, cy - 5),
        w: 80,
        h: 10,
        zIndex: 1,
        margin: 0,
    };

    switch (type) {
        case 'text':
            return {
                ...base,
                binding: null,
                freeText: { pt: 'Text', en: 'Text', fr: 'Text', de: 'Text', es: 'Text' },
                fontFamily: 'Arial',
                fontSize: 14,
                bold: false,
                italic: false,
                colour: '#333333',
                textAlign: 'left',
                bgColour: 'transparent',
                borderWidth: 0,
                borderColour: '#000000',
                borderRadius: 0,
                padding: 4,
            };
        case 'image':
            return {
                ...base,
                w: 60,
                h: 40,
                src: '',
            };
        case 'qr':
            return {
                ...base,
                w: 30,
                h: 30,
                x: Math.max(0, cx - 15),
                y: Math.max(0, cy - 15),
                binding: '{{QrCodeImageUrl}}',
                freeText: '',
            };
        case 'barcode':
            return {
                ...base,
                w: 80,
                h: 20,
                y: Math.max(0, cy - 10),
                binding: '{{BarcodeImageUrl}}',
                freeText: '',
            };
        case 'shape':
            return {
                ...base,
                w: 80,
                h: 20,
                shapeType: 'rect',
                fillColour: '#ffffff',
                borderColour: '#000000',
                borderWidth: 1,
            };
        default:
            return base;
    }
};

// Clamp element position to canvas bounds
export const clampElement = (el, canvasWMm, canvasHMm) => ({
    ...el,
    x: Math.max(0, Math.min(el.x, canvasWMm - el.w)),
    y: Math.max(0, Math.min(el.y, canvasHMm - el.h)),
});

// Shrink-if-larger then pull inside the page — used on page size/orientation changes
// so no element is ever stranded off-page (unreachable and dropped from print).
export const fitElementToPage = (el, canvasWMm, canvasHMm) => {
    const w = Math.min(el.w, canvasWMm);
    const h = Math.min(el.h, canvasHMm);
    return {
        ...el,
        w,
        h,
        x: Math.max(0, Math.min(el.x, canvasWMm - w)),
        y: Math.max(0, Math.min(el.y, canvasHMm - h)),
    };
};

// Safe-URL guard for image sources (Safe URL Validation pattern, FEAT-001).
// Only http(s) URLs render — rejects javascript:, data:, and any malformed URL.
// Used as defense-in-depth on every image render path (manual + AI-sourced).
export const isSafeImageUrl = (url) => {
    if (!url) return false;
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
    } catch {
        return false;
    }
};

export const LOGO_BINDING = '{{VoucherTemplateConfig.LogoImage}}';
export const BACKGROUND_BINDING = '{{VoucherTemplateConfig.BackgroundImage}}';
export const TERMS_INCLUDED_BINDING = '{{VoucherType.VoucherTermsIncluded}}';
export const TERMS_EXCLUDED_BINDING = '{{VoucherType.VoucherTermsExcluded}}';
export const CONDITIONS_BINDING = '{{VoucherType.Tec.Description}}';
export const DESCRIPTION_BINDING = '{{VoucherType.Description}}';

// Binding options available for dynamic fields.
// Values are the full Handlebars token strings matching TemplateBindingCatalog.cs;
// types = element types whose inspector offers the binding (an image URL in a text box prints the URL).
export const BINDING_OPTIONS = [
    { value: '{{VoucherType.Title}}',             labelKey: 'Bindings.VoucherType.Title',        types: ['text'] },
    { value: DESCRIPTION_BINDING,                 labelKey: 'Bindings.VoucherType.Description',  types: ['text'] },
    { value: '{{VoucherType.Value}}',             labelKey: 'Bindings.VoucherType.Value',        types: ['text'] },
    { value: CONDITIONS_BINDING,                  labelKey: 'Bindings.VoucherType.Conditions',   types: ['text'] },
    { value: TERMS_INCLUDED_BINDING,              labelKey: 'Bindings.VoucherType.TermsIncluded', types: ['text'] },
    { value: TERMS_EXCLUDED_BINDING,              labelKey: 'Bindings.VoucherType.TermsExcluded', types: ['text'] },
    { value: '{{VoucherCode}}',                   labelKey: 'Bindings.Voucher.Code',             types: ['text', 'qr', 'barcode'] },
    { value: '{{GuestName}}',                     labelKey: 'Bindings.Voucher.GuestName',        types: ['text'] },
    { value: '{{ValidFrom}}',                     labelKey: 'Bindings.Voucher.ValidFrom',        types: ['text'] },
    { value: '{{ValidTo}}',                       labelKey: 'Bindings.Voucher.ValidTo',          types: ['text'] },
    { value: '{{IssueDate}}',                     labelKey: 'Bindings.Voucher.IssueDate',        types: ['text'] },
    { value: '{{Amount}}',                        labelKey: 'Bindings.Voucher.Amount',           types: ['text'] },
    { value: '{{Currency}}',                      labelKey: 'Bindings.Voucher.Currency',         types: ['text'] },
    { value: '{{From}}',                          labelKey: 'Bindings.Gift.From',                types: ['text'] },
    { value: '{{To}}',                            labelKey: 'Bindings.Gift.To',                  types: ['text'] },
    { value: '{{Message}}',                       labelKey: 'Bindings.Gift.Message',             types: ['text'] },
    { value: '{{HotelName}}',                     labelKey: 'Bindings.VoucherTemplateConfig.HotelName', types: ['text'] },
    { value: LOGO_BINDING,                        labelKey: 'Bindings.VoucherTemplateConfig.LogoImage', types: ['image'] },
    { value: BACKGROUND_BINDING,                  labelKey: 'Bindings.VoucherTemplateConfig.BackgroundImage', types: ['image'] },
    // render as the voucher QR on the server; kept for elements saved with them, offered on no element
    { value: '{{QrCodeImageUrl}}',                labelKey: 'Bindings.QrCode.Image',             types: [] },
    { value: '{{BarcodeImageUrl}}',               labelKey: 'Bindings.Barcode.Image',            types: [] },
];

export const getBindingOptions = (type) => BINDING_OPTIONS.filter(b => b.types.includes(type));

// Select options of an inspector; an element saved with a binding no longer offered for its type keeps showing it
export const getBindingSelectOptions = (type, current) => {
    const options = getBindingOptions(type).map(b => ({ value: b.value, label: b.value }));
    if (current && !options.some(o => o.value === current)) options.push({ value: current, label: current });
    return options;
};

// "Show" condition of an element: a flag of the server render context (ApiVoucherRS); no value = always
export const VISIBILITY_OPTIONS = [
    { value: '',                 labelKey: 'TemplateDesigner.Inspector.Visibility.Always' },
    { value: 'IsGift',           labelKey: 'TemplateDesigner.Inspector.Visibility.IsGift' },
    { value: 'HasLogo',          labelKey: 'TemplateDesigner.Inspector.Visibility.HasLogo' },
    { value: 'HasConditions',    labelKey: 'TemplateDesigner.Inspector.Visibility.HasConditions' },
    { value: 'HasTermsIncluded', labelKey: 'TemplateDesigner.Inspector.Visibility.HasTermsIncluded' },
    { value: 'HasTermsExcluded', labelKey: 'TemplateDesigner.Inspector.Visibility.HasTermsExcluded' },
    // "Mostrar Preço" of the voucher type (wizard step 8): the type decides, the template follows
    { value: 'VoucherTemplateConfig.ShowPrice', labelKey: 'TemplateDesigner.Inspector.Visibility.ShowPrice' },
];
const VISIBILITY_FLAGS = VISIBILITY_OPTIONS.map(o => o.value).filter(Boolean);

// Sample values for preview rendering.
// Keys are the token strings (with braces) that TemplateCompiler emits verbatim.
export const SAMPLE_BINDINGS = {
    '{{VoucherType.Title}}':             'Spa & Wellness Day',
    '{{VoucherType.Description}}':       'Enjoy a full day of relaxation and treatments.',
    '{{VoucherType.Value}}':             '150,00 €',
    '{{VoucherType.Tec.Description}}':   'Válido mediante reserva prévia, sujeito a disponibilidade. Não acumulável com outras campanhas. Não reembolsável em dinheiro.',
    '{{VoucherCode}}':                   'VCHR-2024-001',
    '{{GuestName}}':                     'João Silva',
    '{{ValidFrom}}':                     '2024-01-01',
    '{{ValidTo}}':                       '2024-12-31',
    '{{IssueDate}}':                     '2024-06-01',
    '{{Amount}}':                        '150,00 €',
    '{{Currency}}':                      'EUR',
    '{{From}}':                          'Maria Santos',
    '{{To}}':                            'João Silva',
    '{{Message}}':                       'Parabéns! Aproveita este dia só para ti.',
    '{{VoucherType.VoucherTermsIncluded}}': '• Pequeno-almoço para duas pessoas\n• Acesso ao spa',
    '{{VoucherType.VoucherTermsExcluded}}': '• Bebidas\n• Tratamentos adicionais',
    '{{VoucherTemplateConfig.LogoImage}}': '',
    '{{VoucherTemplateConfig.BackgroundImage}}': '',
    '{{HotelName}}':                     'Hotel Example',
    '{{QrCodeImageUrl}}':                '',
    '{{BarcodeImageUrl}}':               '',
};

export const FONT_FAMILIES = [
    'Arial',
    'Times New Roman',
    'Georgia',
    'Courier New',
    'Verdana',
    'Helvetica',
    'Trebuchet MS',
];

export const ELEMENT_TYPES = ['text', 'image', 'qr', 'barcode', 'shape'];

// Standard starter elements seeded onto a blank new template (the set every system
// voucher template carries); positions derive from the page size.
export const createStarterElements = (wMm, hMm) => {
    const cx = (w) => Math.round((wMm - w) / 2 * 10) / 10;

    const text = (over) => ({
        id: generateId(),
        type: 'text',
        binding: null,
        freeText: '',
        textAlign: 'center',
        ...over,
    });

    return [
        { id: generateId(), type: 'image', binding: LOGO_BINDING, visibleWhen: 'HasLogo', src: '', x: cx(40), y: 8, w: 40, h: 20, zIndex: 1 },
        text({ binding: '{{VoucherType.Title}}', x: 15, y: 32, w: wMm - 30, h: 15, zIndex: 2, fontSize: 26, bold: true }),
        text({ binding: '{{VoucherType.Description}}', x: 20, y: 50, w: wMm - 40, h: 20, zIndex: 3, fontSize: 13 }),
        text({ binding: '{{Amount}}', x: 15, y: Math.round(hMm * 0.42), w: wMm - 30, h: 12, zIndex: 4, fontSize: 22, bold: true }),
        text({ binding: '{{ValidTo}}', x: 15, y: Math.round(hMm * 0.42) + 15, w: wMm - 30, h: 8, zIndex: 5, fontSize: 11, colour: '#666666' }),
        { id: generateId(), type: 'qr', binding: '{{VoucherCode}}', freeText: '', x: wMm - 40, y: hMm - 48, w: 28, h: 28, zIndex: 6 },
        text({ binding: '{{VoucherCode}}', x: wMm - 52, y: hMm - 17, w: 46, h: 7, zIndex: 7, fontSize: 9 }),
        text({ binding: '{{VoucherType.Tec.Description}}', x: 12, y: hMm - 32, w: wMm - 64, h: 24, zIndex: 8, fontSize: 7, colour: '#666666', textAlign: 'left' }),
    ];
};

// Designer binding token → token that exists in the server render context (VoucherContexts);
// unlisted tokens already match and pass through. QR and barcode share the QrCode field.
const RENDER_TOKEN_MAP = {
    '{{VoucherType.Value}}': '{{VoucherValue}}',
    '{{VoucherCode}}':       '{{VoucherId}}',
    '{{GuestName}}':         '{{name}}',
    '{{ValidFrom}}':         '{{ValidFromDate}}',
    '{{ValidTo}}':           '{{ValidUntilDate}}',
    '{{IssueDate}}':         '{{CreatedAtDate}}',
    '{{Amount}}':            '{{VoucherValue}}',
    '{{QrCodeImageUrl}}':    '{{QrCode}}',
    '{{BarcodeImageUrl}}':   '{{QrCode}}',
};

const toRenderToken = (binding) => RENDER_TOKEN_MAP[binding] || binding;

// Rich text fields (sanitized markup, or plain text from before rich text) go through the server
// richText helper (Voucher.Library RichText); each term of a voucher type is one rich text field
const RICH_TEXT_TOKEN_MAP = {
    // description: plain text from before rich text keeps one paragraph per line, as in the backoffice editor
    [DESCRIPTION_BINDING]:    '{{richText VoucherType.Description "p"}}',
    [CONDITIONS_BINDING]:     '{{richText VoucherType.Tec.Description}}',
    [TERMS_INCLUDED_BINDING]: '{{#each VoucherType.VoucherTermsIncluded}}{{richText Description}}{{/each}}',
    [TERMS_EXCLUDED_BINDING]: '{{#each VoucherType.VoucherTermsExcluded}}{{richText Description}}{{/each}}',
};

// Element HTML inside its "Show" condition; a background image is only emitted when the voucher type has one
const wrapVisibility = (el, html) => {
    if (!html) return html;

    let out = html;
    if (el.type === 'image' && !el.src && el.binding === BACKGROUND_BINDING) {
        out = `{{#if HasBackground}}${out}{{/if}}`;
    }
    if (VISIBILITY_FLAGS.includes(el.visibleWhen)) {
        out = `{{#if ${el.visibleWhen}}}${out}{{/if}}`;
    }
    return out;
};

// Preset drop shadows (element.shadow), shared by canvas, preview and print
export const SHADOW_CSS = {
    soft:   '0 1px 4px rgba(0,0,0,0.3)',
    medium: '0 3px 10px rgba(0,0,0,0.35)',
    strong: '0 6px 18px rgba(0,0,0,0.45)',
};

export const getShadowCss = (shadow) => SHADOW_CSS[shadow] || null;

// filter equivalents for clip-path shapes: box-shadow gets clipped away, drop-shadow follows the outline
const DROP_SHADOW_CSS = {
    soft:   'drop-shadow(0 1px 3px rgba(0,0,0,0.35))',
    medium: 'drop-shadow(0 3px 8px rgba(0,0,0,0.4))',
    strong: 'drop-shadow(0 6px 14px rgba(0,0,0,0.5))',
};

export const getDropShadowCss = (shadow) => DROP_SHADOW_CSS[shadow] || null;

// clip-path shapes are fill-only: a border would be clipped away with the box
export const SHAPE_CLIP_PATHS = {
    triangle: 'polygon(50% 0%, 0% 100%, 100% 100%)',
    diamond:  'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
    star:     'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
};

export const isClipShape = (el) => el && el.type === 'shape' && !!SHAPE_CLIP_PATHS[el.shapeType];

// Vertical alignment of a text box → flex justify-content value (null = default top)
export const getVerticalAlignCss = (verticalAlign) => {
    if (verticalAlign === 'middle') return 'center';
    if (verticalAlign === 'bottom') return 'flex-end';
    return null;
};

// The generated HTML is persisted and later rendered in customer emails/PDFs, so every
// user-typed value that lands in it must be escaped for its context.
const escapeHtml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeAttr = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
// css url() context: percent-encode the delimiters a URL could use to break out
const CSS_URL_ESCAPES = { '(': '%28', ')': '%29', "'": '%27', '"': '%22', '\\': '%5C', '<': '%3C', '>': '%3E' };
const sanitizeCssUrl = (u) => u.replace(/[()'"\\<>\s]/g, (c) => CSS_URL_ESCAPES[c] || encodeURIComponent(c));
// colours land in style attributes: only plain colour syntaxes may pass through
const SAFE_COLOUR_RE = /^(#[0-9a-fA-F]{3,8}|rgba?\(\s*[\d.,\s%]+\)|transparent)$/;
const safeColour = (c, fallback) => (typeof c === 'string' && SAFE_COLOUR_RE.test(c.trim())) ? c.trim() : fallback;

// opts.emitTokens builds the persistable HTML: bindings become Handlebars tokens for the
// server to resolve, instead of the preview sample values.
export function buildPrintHtml(template, elements, barcodeDataUrls = {}, lang = DEFAULT_LANG, opts = {}) {
    const pageSize = template.pageSize || 'A4';
    const orientation = template.orientation || 'portrait';
    // Use exact ISO paper dimensions for the wrapper — avoids rounding drift from px→mm
    const paper = getPaperDimsMm(pageSize, orientation);
    const W_mm = paper.w;
    const H_mm = paper.h;

    const bgStyle = `background-color:${safeColour(template.bgColour, '#ffffff')};`;

    // bg image as its own layer: CSS background-image has no opacity of its own
    const bgOpacity = (template.bgImageOpacity ?? 100) / 100;
    const bgUrl = isSafeImageUrl(template.bgImage) ? sanitizeCssUrl(template.bgImage) : null;
    const bgLayer = bgUrl
        ? `<div style="position:absolute;top:0;left:0;right:0;bottom:0;background-image:url('${bgUrl}');background-size:cover;background-position:center;opacity:${bgOpacity};z-index:0;"></div>`
        : '';

    const pageWpx = W_mm * MM_TO_PX_96DPI;
    const pageHpx = H_mm * MM_TO_PX_96DPI;

    const renderElement = (el) => {
        const m      = (el.margin || 0) * MM_TO_PX_96DPI;
        const leftN  = (el.x * MM_TO_PX_96DPI) + m;
        const topN   = (el.y * MM_TO_PX_96DPI) + m;

        // off-page elements must be dropped: their layout overflow makes Chrome emit an
        // extra blank printed page even though the CSS clip hides them
        if (leftN >= pageWpx || topN >= pageHpx) return '';

        const left   = leftN.toFixed(2);
        const top    = topN.toFixed(2);
        const width  = Math.max(0, (el.w * MM_TO_PX_96DPI) - 2 * m).toFixed(2);
        const height = Math.max(0, (el.h * MM_TO_PX_96DPI) - 2 * m).toFixed(2);
        const zIndex = el.zIndex || 1;
        const opacityCss = (el.opacity ?? 100) < 100 ? `opacity:${(el.opacity ?? 100) / 100};` : '';
        // clip shapes use a drop-shadow filter instead (box-shadow would halo the box);
        // the wrapper carries the radius so the shadow hugs the rounded corners
        const shadow = isClipShape(el) ? null : getShadowCss(el.shadow);
        const shadowCss = shadow ? `box-shadow:${shadow};` : '';
        const radiusCss = (el.borderRadius || 0) > 0 ? `border-radius:${el.borderRadius}px;` : '';
        const rotationCss = (el.rotation || 0) !== 0 ? `transform:rotate(${el.rotation}deg);` : '';
        const pos = `position:absolute;left:${left}px;top:${top}px;width:${width}px;height:${height}px;z-index:${zIndex};box-sizing:border-box;overflow:hidden;${opacityCss}${shadowCss}${radiusCss}${rotationCss}`;

        // rich text tokens are markup for the server helper, emitted as they are
        const richToken = opts.emitTokens && el.type === 'text' ? RICH_TEXT_TOKEN_MAP[el.binding] : null;
        const value = richToken
            || (el.binding
                ? (opts.emitTokens ? escapeHtml(toRenderToken(el.binding)) : (SAMPLE_BINDINGS[el.binding] || el.binding))
                : escapeHtml(getPreviewText(el.freeText, lang) || ''));

        switch (el.type) {
            case 'text': {
                const fw  = el.bold   ? 'bold'   : 'normal';
                const fs  = el.italic ? 'italic' : 'normal';
                const td  = el.underline ? 'underline' : 'none';
                const bw  = el.borderWidth || 0;
                const bdr = bw > 0 ? `${bw}px solid ${safeColour(el.borderColour, '#000000')}` : 'none';
                const vAlign = getVerticalAlignCss(el.verticalAlign);
                const vAlignCss = vAlign ? `display:flex;flex-direction:column;justify-content:${vAlign};` : '';
                // rendered values can be longer than the designed box (long descriptions):
                // let text boxes grow downwards instead of clipping mid-line
                const textPos = pos
                    .replace(`height:${height}px`, `min-height:${height}px`)
                    .replace('overflow:hidden', 'overflow:visible');
                // rich text brings its own paragraphs and lists: pre-wrap would add the markup's line breaks
                const whiteSpace = richToken ? 'normal' : 'pre-wrap';
                // px (not pt) so print matches the canvas
                return `<div${richToken ? ' class="rt"' : ''} style="${textPos}${vAlignCss}font-family:${el.fontFamily||'Arial'};font-size:${el.fontSize||14}px;font-weight:${fw};font-style:${fs};text-decoration:${td};color:${safeColour(el.colour, '#333333')};text-align:${el.textAlign||'left'};background-color:${safeColour(el.bgColour, 'transparent')};border:${bdr};border-radius:${el.borderRadius||0}px;padding:${el.padding??4}px;line-height:${el.lineHeight||1.2};letter-spacing:${el.letterSpacing||0}px;white-space:${whiteSpace};word-break:break-word;">${value||''}</div>`;
            }
            case 'image': {
                const src = el.src
                    ? (isSafeImageUrl(el.src) ? escapeAttr(el.src) : '')
                    : (opts.emitTokens && el.binding ? toRenderToken(el.binding) : '');
                if (src) {
                    return `<img src="${src}" style="${pos}object-fit:${el.fit || 'contain'};border-radius:${el.borderRadius||0}px;" />`;
                }
                if (opts.emitTokens) return '';
                return `<div style="${pos}display:flex;align-items:center;justify-content:center;border:1px dashed #adb5bd;color:#adb5bd;font-size:10px;font-family:Arial;">Image</div>`;
            }
            case 'barcode': {
                if (opts.emitTokens) {
                    return `<div style="${pos}display:flex;align-items:center;justify-content:center;"><img src="{{QrCode}}" style="max-width:100%;max-height:100%;object-fit:contain;" /></div>`;
                }
                const dataUrl = barcodeDataUrls[el.id];
                if (dataUrl) {
                    return `<div style="${pos}display:flex;align-items:center;justify-content:center;"><img src="${dataUrl}" style="max-width:100%;max-height:100%;object-fit:contain;" /></div>`;
                }
                return `<div style="${pos}display:flex;align-items:center;justify-content:center;border:1px dashed #adb5bd;color:#adb5bd;font-size:10px;font-family:Arial;">Barcode</div>`;
            }
            case 'qr': {
                if (opts.emitTokens) {
                    // server QR is B&W: screen blend turns black into qrColour, multiply overlay
                    // turns white into qrBgColour; isolation stops the blends leaking behind
                    const qrFg = safeColour(el.qrColour, '#000000');
                    const qrBg = safeColour(el.qrBgColour, '#ffffff');
                    if (qrFg !== '#000000' || qrBg !== '#ffffff') {
                        // display:block + object-fit:fill: an inline img leaves a baseline gap
                        // below and contain leaves rounding slivers where the wrapper colour
                        // bleeds through as light lines (the QR is square, fill is lossless)
                        return `<div style="${pos}display:flex;align-items:center;justify-content:center;">`
                            + `<div style="height:100%;max-width:100%;aspect-ratio:1;position:relative;background-color:${qrFg};isolation:isolate;overflow:hidden;">`
                            + `<img src="{{QrCode}}" style="display:block;width:100%;height:100%;object-fit:fill;mix-blend-mode:screen;" />`
                            + `<div style="position:absolute;top:0;left:0;right:0;bottom:0;background-color:${qrBg};mix-blend-mode:multiply;"></div>`
                            + `</div></div>`;
                    }
                    return `<div style="${pos}display:flex;align-items:center;justify-content:center;"><img src="{{QrCode}}" style="max-width:100%;max-height:100%;object-fit:contain;" /></div>`;
                }
                // barcodeDataUrls carries pre-rendered data URLs for barcode AND qr elements
                const qrDataUrl = barcodeDataUrls[el.id];
                if (qrDataUrl) {
                    return `<div style="${pos}display:flex;align-items:center;justify-content:center;"><img src="${qrDataUrl}" style="max-width:100%;max-height:100%;object-fit:contain;" /></div>`;
                }
                const qrVal = value || 'QR';
                return `<div style="${pos}display:flex;align-items:center;justify-content:center;border:1px solid #000;font-size:9px;font-family:monospace;text-align:center;padding:4px;">[QR]<br/><span style="font-size:7px;word-break:break-all;">${qrVal}</span></div>`;
            }
            case 'shape': {
                if (el.shapeType === 'line') {
                    return `<div style="${pos}display:flex;align-items:center;"><div style="width:100%;height:${el.borderWidth ?? 1}px;background-color:${safeColour(el.borderColour, '#000000')};"></div></div>`;
                }
                const fill = safeColour(el.fillColour, '#ffffff');
                // ?? (not ||): an explicit borderWidth of 0 must render as no border
                const bw = el.borderWidth ?? 1;
                const shapeBorder = bw > 0 ? `border:${bw}px solid ${safeColour(el.borderColour, '#000000')};` : '';
                if (el.shapeType === 'ellipse') {
                    return `<div style="${pos}background-color:${fill};${shapeBorder}border-radius:50%;"></div>`;
                }
                const clip = SHAPE_CLIP_PATHS[el.shapeType];
                if (clip) {
                    // shadow on the wrapper, clip on the child: filter runs before clip-path
                    // on the same element and the shadow would be clipped away
                    const drop = getDropShadowCss(el.shadow);
                    return `<div style="${pos}${drop ? `filter:${drop};` : ''}"><div style="width:100%;height:100%;background-color:${fill};clip-path:${clip};"></div></div>`;
                }
                return `<div style="${pos}background-color:${fill};${shapeBorder}border-radius:${el.borderRadius||0}px;"></div>`;
            }
            default:
                return '';
        }
    };

    // conditions only on the server render; previews show every element
    const elHtml = elements
        .map(el => (opts.emitTokens ? wrapVisibility(el, renderElement(el)) : renderElement(el)))
        .join('\n');

    // compact paragraphs and lists of rich text (browser defaults push the text over the next element);
    // only when the template has rich text, so templates without it keep the same HTML
    const richCss = elHtml.includes('class="rt"')
        ? '\n.rt p,.rt ul,.rt ol{margin:0 0 0.3em 0;}.rt ul,.rt ol{padding-left:1.2em;}.rt li{margin:0;}.rt>:last-child{margin-bottom:0;}'
        : '';

    // @page margin 0: positions are absolute from the page origin (pageMarginX/Y are a
    // design guide only); a real margin would shift and clip the layout
    return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<style>
@page{size:${pageSize} ${orientation};margin:0;}
html,body{margin:0;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;overflow:hidden;}${richCss}
</style>
</head><body>
<div style="position:absolute;top:0;left:0;width:${W_mm}mm;height:${H_mm}mm;overflow:hidden;${bgStyle}">${bgLayer}${elHtml}</div>
</body></html>`;
}
